/**
 * Operating Hours Utility Functions
 * Handles conversion of operating hours to 24-hour format for API payloads
 */

// Types
interface TimeSlot {
  start: string;
  end: string;
}

interface DaySchedule {
  firstHalf: TimeSlot;
  secondHalf: TimeSlot;
  isClosed: boolean;
}

type OperatingHours = Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  DaySchedule
>;

/**
 * Converts operating hours to 24-hour format for API payload
 * Handles closed days and ensures proper format
 * 
 * @param operatingHours - Operating hours object with all days
 * @returns Converted operating hours in 24-hour format
 */
export function convertOperatingHoursTo24Hour(operatingHours: OperatingHours): OperatingHours {
  const converted: any = {};
  
  const days: Array<keyof OperatingHours> = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ];
  
  days.forEach(day => {
    const dayData = operatingHours[day];
    
    // If day is closed, clear all times
    if (dayData.isClosed) {
      converted[day] = {
        firstHalf: {
          start: '',
          end: ''
        },
        secondHalf: {
          start: '',
          end: ''
        },
        isClosed: true
      };
    } else {
      // Day is open, convert times to 24-hour format
      converted[day] = {
        firstHalf: {
          start: convertTo24Hour(dayData.firstHalf.start),
          end: convertTo24Hour(dayData.firstHalf.end)
        },
        secondHalf: {
          start: convertTo24Hour(dayData.secondHalf.start),
          end: convertTo24Hour(dayData.secondHalf.end)
        },
        isClosed: false
      };
    }
  });
  
  return converted as OperatingHours;
}

/**
 * Converts a time string to 24-hour format
 * Handles both 12-hour format (9:00 AM) and 24-hour format (09:00)
 * 
 * @param time - Time string in any format
 * @returns Time string in 24-hour format (HH:mm)
 */
function convertTo24Hour(time: string): string {
  if (!time) return '';
  
  // Check if it's 12-hour format (has AM/PM)
  if (time.includes('AM') || time.includes('PM') || time.includes('am') || time.includes('pm')) {
    const trimmed = time.trim();
    const parts = trimmed.split(/\s+/);
    
    let timePart: string;
    let modifier: string;
    
    if (parts.length === 2) {
      // Format: "9:00 AM"
      [timePart, modifier] = parts;
    } else {
      // Format: "9:00AM" - extract modifier from end
      const match = trimmed.match(/^(.+?)(AM|PM|am|pm)$/);
      if (match) {
        timePart = match[1];
        modifier = match[2];
      } else {
        return time; // Return as-is if format is unexpected
      }
    }
    
    const [hours, minutes] = timePart.split(':');
    let h = parseInt(hours, 10);
    
    // Convert to 24-hour format
    if (modifier.toUpperCase() === 'PM' && h !== 12) {
      h += 12;
    }
    if (modifier.toUpperCase() === 'AM' && h === 12) {
      h = 0;
    }
    
    return `${h.toString().padStart(2, '0')}:${minutes}`;
  }
  
  // Already in 24-hour format or other format - return as-is
  return time;
}
